import { Box, Typography, Grid, Button } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';

import { SectionContent, GoBackButton, StyledList } from '../../components';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import { SECTION_SPACING, ELEMENT_SPACING } from '../../constants';
import { GLOBAL_MACRO_CONTENT } from '../../data';
import globalMacroLogo from '../../assets/icons/global-macro-logo-blue.svg';

const GlobalMacroPage = () => {
  return (
    <>
      <SEO
        title={seoData.globalMacro.title}
        content={seoData.globalMacro.content}
        keywords={seoData.globalMacro.keywords}
        canonical={seoData.globalMacro.canonical}
        ogImage={seoData.globalMacro.ogImage}
        ogType={seoData.globalMacro.ogType}
        structuredData={seoData.globalMacro.structuredData}
      />
      <Box sx={{ width: '100%', backgroundColor: THEME.COLORS.NEUTRAL_WHITE }}>
        <GoBackButton backTo={ROUTES.STRATEGIES.ROOT} backText='Strategies' />

        {/* Hero Section with Logo */}
        <Box id='hero' sx={{ mb: SECTION_SPACING }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: THEME.SPACING.SMALL
            }}
          >
            <img
              src={globalMacroLogo}
              alt='Global Macro Logo'
              style={{
                height: '110px',
                objectFit: 'contain'
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
              color: THEME.COLORS.NEUTRAL_GRAY_DARK,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM
            }}
          >
            {GLOBAL_MACRO_CONTENT.TITLE}
          </Typography>
        </Box>

        {/* Introduction Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            About Global Macro
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: 'In a world shaped by inflation shocks, interest rate divergence, geopolitical conflict, deglobalization pressures and structural shifts in capital flows, equity investing demands more than passive exposure. It demands a global perspective on macro forces and the ability to translate them into precise equity positioning.',
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              },
              {
                text: "Global Equity Macro, a systematic strategy by MarQ Capital, is designed to do exactly that. The strategy allocates risk exclusively across a curated set of the world's most liquid and influential equity indices, expressing macroeconomic views through equity markets rather than across asset classes.",
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              },
              {
                text: 'This is not traditional equity diversification. It is macro-driven equity conviction, powered by data, disciplined risk management and adaptive learning.',
                fontSize: '1.3rem',
                marginBottom: 0,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />
        </Box>

        {/* A Fragmented Macro World Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            {GLOBAL_MACRO_CONTENT.FRAGMENTED_WORLD.TITLE}
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: GLOBAL_MACRO_CONTENT.FRAGMENTED_WORLD.INTRO,
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />

          <Box
            sx={{
              paddingLeft: {
                xs: THEME.SPACING.MEDIUM,
                sm: THEME.SPACING.MEDIUM,
                md: THEME.SPACING.XL
              },
              mb: ELEMENT_SPACING
            }}
          >
            <StyledList
              variant='blue'
              items={GLOBAL_MACRO_CONTENT.FRAGMENTED_WORLD.ITEMS}
            />
          </Box>

          <SectionContent
            initialSpacing={false}
            text={GLOBAL_MACRO_CONTENT.FRAGMENTED_WORLD.CONCLUSION.map(
              (paragraph, index) => ({
                text: paragraph,
                fontSize: '1.3rem',
                marginBottom:
                  index <
                  GLOBAL_MACRO_CONTENT.FRAGMENTED_WORLD.CONCLUSION.length - 1
                    ? ELEMENT_SPACING
                    : 0,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              })
            )}
          />
        </Box>

        {/* The Strategic Equity Universe Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            {GLOBAL_MACRO_CONTENT.STRATEGIC_UNIVERSE.TITLE}
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: GLOBAL_MACRO_CONTENT.STRATEGIC_UNIVERSE.INTRO,
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />

          <Box
            sx={{
              paddingLeft: {
                xs: THEME.SPACING.MEDIUM,
                sm: THEME.SPACING.MEDIUM,
                md: THEME.SPACING.XL
              },
              mb: ELEMENT_SPACING
            }}
          >
            <StyledList
              variant='blue'
              items={GLOBAL_MACRO_CONTENT.STRATEGIC_UNIVERSE.ITEMS}
            />
          </Box>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: GLOBAL_MACRO_CONTENT.STRATEGIC_UNIVERSE.CONCLUSION,
                fontSize: '1.3rem',
                marginBottom: 0,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />
        </Box>

        {/* Key Challenges Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            {GLOBAL_MACRO_CONTENT.CHALLENGES.TITLE}
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: GLOBAL_MACRO_CONTENT.CHALLENGES.INTRO,
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />

          <Box sx={{ mb: ELEMENT_SPACING }}>
            <StyledList
              variant='numbered'
              items={GLOBAL_MACRO_CONTENT.CHALLENGES.ITEMS.map(item => ({
                title: item.TITLE,
                text: item.TEXT
              }))}
            />
          </Box>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: GLOBAL_MACRO_CONTENT.CHALLENGES.CONCLUSION,
                fontSize: '1.3rem',
                marginBottom: 0,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />
        </Box>

        {/* The MarQ Capital Approach Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            {GLOBAL_MACRO_CONTENT.SOLUTION.TITLE}
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: GLOBAL_MACRO_CONTENT.SOLUTION.INTRO,
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />

          <Box sx={{ mb: ELEMENT_SPACING }}>
            <StyledList
              variant='numbered'
              items={GLOBAL_MACRO_CONTENT.SOLUTION.ITEMS.map(item => ({
                title: item.TITLE,
                text: item.TEXT
              }))}
            />
          </Box>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: GLOBAL_MACRO_CONTENT.SOLUTION.CONCLUSION,
                fontSize: '1.3rem',
                marginBottom: 0,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />
        </Box>

        {/* Why Global Equity Macro Section */}
        <Box
          sx={{
            mb: SECTION_SPACING,
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            borderRadius: '8px',
            padding: { xs: 3, sm: 4, md: 5 },
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.NEUTRAL_WHITE,
              mb: ELEMENT_SPACING
            }}
          >
            {GLOBAL_MACRO_CONTENT.OBJECTIVE.TITLE}
          </Typography>

          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.3rem',
              color: THEME.COLORS.NEUTRAL_WHITE,
              fontStyle: 'italic',
              lineHeight: 1.8,
              mb: ELEMENT_SPACING
            }}
          >
            {GLOBAL_MACRO_CONTENT.OBJECTIVE.INTRO}
          </Typography>

          {GLOBAL_MACRO_CONTENT.OBJECTIVE.BODY.map((paragraph, index) => (
            <Typography
              key={index}
              sx={{
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                fontSize: '1.3rem',
                color: THEME.COLORS.NEUTRAL_WHITE,
                fontStyle: 'italic',
                lineHeight: 1.8,
                mb:
                  index < GLOBAL_MACRO_CONTENT.OBJECTIVE.BODY.length - 1
                    ? ELEMENT_SPACING
                    : 0
              }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>

        {/* Related Documents Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Related Documents
          </Typography>
          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant='outlined'
                startIcon={<DescriptionIcon />}
                disabled
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '80px',
                  padding: 3,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.1rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                  textTransform: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '&:hover': {
                    borderColor: THEME.COLORS.PRIMARY_BLUE,
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE_LIGHT + '22'
                  }
                }}
              >
                <Box>Full Factsheet</Box>
                <Box sx={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                  Complete strategy overview
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant='outlined'
                startIcon={<DescriptionIcon />}
                disabled
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '80px',
                  padding: 3,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.1rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                  textTransform: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '&:hover': {
                    borderColor: THEME.COLORS.PRIMARY_BLUE,
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE_LIGHT + '22'
                  }
                }}
              >
                <Box>Strategy Description</Box>
                <Box sx={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                  Key metrics & methodology
                </Box>
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Get in Touch
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: 'For more information about Global Equity Macro or to discuss how this strategy may complement your portfolio, please contact our team.',
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              },
              {
                text: 'This information has been prepared solely for informational purposes and is not an offer to buy or sell or a solicitation of an offer to buy or sell any security or instrument or to participate in any trading strategy. Past performance is not necessarily indicative of future results.',
                fontSize: '1rem',
                marginBottom: 0,
                color: THEME.COLORS.NEUTRAL_GRAY,
                fontStyle: 'italic'
              }
            ]}
          />
        </Box>
      </Box>
    </>
  );
};

export default GlobalMacroPage;
